// Define sorting configurations for different sections

// Sort direction
export type SortDirection = 'asc' | 'desc';

// Base interface for items
interface BaseItem {
  name: string;
  tags: string[];
  period?: {
    start: string;
    end?: string;
  };
}

// Sort criteria types - basic sorting options
export type BasicSortCriteria = 'name' | 'startDate' | 'endDate' | 'none';

// Sort configuration interface
export interface SortConfig {
  criteria: BasicSortCriteria;
  direction: SortDirection;
  priorityTags?: string[]; // Optional priority tags that should appear first, order matters
}

// Default sort configurations for each section
export const defaultSortConfigs: Record<string, SortConfig> = {
  portfolio: {
    criteria: 'name',
    direction: 'asc',
    priorityTags: ['Unicorn'],
  },
  experience: {
    criteria: 'startDate',
    direction: 'desc',
    priorityTags: ['A','B','C','D','Active'],
  },
  education: {
    criteria: 'none',
    direction: 'desc',
  },
};

// Compare items based on name
const compareByName = (a: BaseItem, b: BaseItem, direction: SortDirection): number => {
  const nameA = a.name.toLowerCase();
  const nameB = b.name.toLowerCase();
  
  if (direction === 'asc') {
    return nameA.localeCompare(nameB);
  }
  return nameB.localeCompare(nameA);
};

// Compare items based on start date
const compareByStartDate = (a: BaseItem, b: BaseItem, direction: SortDirection): number => {
  // If period is missing, place at the end (or beginning if descending)
  if (!a.period?.start) return direction === 'asc' ? 1 : -1;
  if (!b.period?.start) return direction === 'asc' ? -1 : 1;
  
  const dateA = new Date(a.period.start);
  const dateB = new Date(b.period.start);
  
  if (direction === 'asc') {
    return dateA.getTime() - dateB.getTime();
  }
  return dateB.getTime() - dateA.getTime();
};

// Compare items based on end date
const compareByEndDate = (a: BaseItem, b: BaseItem, direction: SortDirection): number => {
  // Handle cases where end date is undefined (ongoing)
  const endDateA = a.period?.end ? new Date(a.period.end) : new Date();
  const endDateB = b.period?.end ? new Date(b.period.end) : new Date();
  
  if (direction === 'asc') {
    return endDateA.getTime() - endDateB.getTime();
  }
  return endDateB.getTime() - endDateA.getTime();
};

// Get priority level of item (lower index = higher priority)
// Returns -1 if item has no priority tags
const getPriorityLevel = (item: BaseItem, priorityTags: string[] = []): number => {
  if (!priorityTags || priorityTags.length === 0) return -1;

  // Find the first (highest priority) tag that matches
  for (let i = 0; i < priorityTags.length; i++) {
    if (item.tags.includes(priorityTags[i])) {
      return i;
    }
  }
  
  return -1; // No priority tags found
};

// Helper for applying main sort criteria
const applyMainSortCriteria = (a: BaseItem, b: BaseItem, config: SortConfig): number => {
  switch (config.criteria) {
    case 'name':
      return compareByName(a, b, config.direction);
    case 'startDate':
      return compareByStartDate(a, b, config.direction);
    case 'endDate':
      return compareByEndDate(a, b, config.direction);
    case 'none':
      return 0; // Maintain original order
    default:
      return 0;
  }
};

// Spotlight-specific types
export type SpotlightSize = "1x1" | "2x1" | "1x2" | "2x2";

export interface SpotlightSortableItem {
  size?: SpotlightSize;
  content: {
    video?: string;
  };
  tags: string[];
  publishedAt?: string;
}

// Helper function to shuffle an array randomly
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Deterministic spotlight sorting function (for dedicated pages)
export function sortSpotlightItems<T extends SpotlightSortableItem>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    // Place larger cards first for better layout
    const aSize = a.size || "1x1";
    const bSize = b.size || "1x1";
    
    // Filler cards always last
    const aIsFiller = a.tags.includes("Filler");
    const bIsFiller = b.tags.includes("Filler");
    if (aIsFiller && !bIsFiller) return 1;
    if (!aIsFiller && bIsFiller) return -1;
    
    // 2x2 cards first
    if (aSize === "2x2" && bSize !== "2x2") return -1;
    if (aSize !== "2x2" && bSize === "2x2") return 1;
    
    // Then 2x1 cards
    if (aSize === "2x1" && bSize !== "2x1") return -1;
    if (aSize !== "2x1" && bSize === "2x1") return 1;
    
    // Then 1x2 cards
    if (aSize === "1x2" && bSize !== "1x2") return -1;
    if (aSize !== "1x2" && bSize === "1x2") return 1;
    
    // Video content next
    if (a.content.video && !b.content.video) return -1;
    if (!a.content.video && b.content.video) return 1;
    
    // Testimonials next
    const aIsTestimonial = a.tags.includes("Testimonial");
    const bIsTestimonial = b.tags.includes("Testimonial");
    if (aIsTestimonial && !bIsTestimonial) return -1;
    if (!aIsTestimonial && bIsTestimonial) return 1;
    
    // Published date (newer first - descending order)
    if (a.publishedAt && b.publishedAt) {
      const dateA = new Date(a.publishedAt);
      const dateB = new Date(b.publishedAt);
      const dateDiff = dateB.getTime() - dateA.getTime();
      if (dateDiff !== 0) return dateDiff;
    }
    
    // Items with publishedAt before items without
    if (a.publishedAt && !b.publishedAt) return -1;
    if (!a.publishedAt && b.publishedAt) return 1;
    
    // Default to original order
    return 0;
  });
}

// Randomized spotlight sorting function (for home page)
export function sortSpotlightItemsRandomized<T extends SpotlightSortableItem>(items: T[]): T[] {
  // Group items by priority/weight
  const groups = {
    size2x2: [] as T[],
    size2x1: [] as T[],
    size1x2: [] as T[],
    video: [] as T[],
    testimonial: [] as T[],
    regular1x1: [] as T[],
    filler: [] as T[]
  };
  
  // Categorize items into groups
  items.forEach(item => {
    const size = item.size || "1x1";
    const isFiller = item.tags.includes("Filler");
    const isVideo = item.content.video;
    const isTestimonial = item.tags.includes("Testimonial");
    
    if (isFiller) {
      groups.filler.push(item);
    } else if (size === "2x2") {
      groups.size2x2.push(item);
    } else if (size === "2x1") {
      groups.size2x1.push(item);
    } else if (size === "1x2") {
      groups.size1x2.push(item);
    } else if (isVideo) {
      groups.video.push(item);
    } else if (isTestimonial) {
      groups.testimonial.push(item);
    } else {
      groups.regular1x1.push(item);
    }
  });
  
  // Shuffle each group randomly and combine in priority order
  return [
    ...shuffleArray(groups.size2x2),
    ...shuffleArray(groups.size2x1),
    ...shuffleArray(groups.size1x2),
    ...shuffleArray(groups.video),
    ...shuffleArray(groups.testimonial),
    ...shuffleArray(groups.regular1x1),
    ...shuffleArray(groups.filler)
  ];
}

// Sort items based on config
export function sortItems<T extends BaseItem>(items: T[], config: SortConfig): T[] {
  // If criteria is 'none', apply only priority tags and maintain original order
  if (config.criteria === 'none') {
    // Only apply priority tags if specified
    if (config.priorityTags && config.priorityTags.length > 0) {
      return [...items].sort((a, b) => {
        const aPriorityLevel = getPriorityLevel(a, config.priorityTags);
        const bPriorityLevel = getPriorityLevel(b, config.priorityTags);
        
        // If both have priority tags
        if (aPriorityLevel >= 0 && bPriorityLevel >= 0) {
          // If same priority level (same tag category), maintain original order
          if (aPriorityLevel === bPriorityLevel) {
            return 0;
          }
          // Lower priority level (index in array) comes first
          return aPriorityLevel - bPriorityLevel;
        }
        
        // If only one has priority, it comes first
        if (aPriorityLevel >= 0 && bPriorityLevel < 0) return -1;
        if (aPriorityLevel < 0 && bPriorityLevel >= 0) return 1;
        
        // If neither has priority, maintain original order
        return 0;
      });
    }
    // If no priority tags, return original order
    return [...items];
  }
  
  // For other criteria, apply regular sorting
  return [...items].sort((a, b) => {
    // Apply priority tags first if specified
    if (config.priorityTags && config.priorityTags.length > 0) {
      const aPriorityLevel = getPriorityLevel(a, config.priorityTags);
      const bPriorityLevel = getPriorityLevel(b, config.priorityTags);
      
      // If both have priority tags
      if (aPriorityLevel >= 0 && bPriorityLevel >= 0) {
        // If same priority level (same tag category), sort by main criteria
        if (aPriorityLevel === bPriorityLevel) {
          return applyMainSortCriteria(a, b, config);
        }
        // Different priority levels - lower priority level (index in array) comes first
        return aPriorityLevel - bPriorityLevel;
      }
      
      // If only one has priority, it comes first
      if (aPriorityLevel >= 0 && bPriorityLevel < 0) return -1;
      if (aPriorityLevel < 0 && bPriorityLevel >= 0) return 1;
    }
    
    // Apply the selected sort criteria
    return applyMainSortCriteria(a, b, config);
  });
}